/**
 * WiseRecipientForm.js
 *
 * Dynamic country-specific bank account form driven by Wise's account-requirements API.
 * Each country/currency combination returns its own unique set of required fields:
 *   India  → accountNumber + ifscCode + legalType + address.{country,city,firstLine,postCode}
 *   UK     → sortCode + accountNumber
 *   EU     → IBAN + BIC
 *   AU     → BSB code + accountNumber
 *   Mexico → CLABE
 *   Brazil → bankCode + agencyNumber + accountNumber + CPF
 *   etc.
 *
 * Fields with refreshRequirementsOnChange=true trigger a re-fetch when changed
 * (e.g. India's accountType: savings vs checking changes required sub-fields).
 *
 * Props:
 *   country    {string}   ISO-3166-alpha2 country code
 *   onComplete {function} Called with serialized GUWallet when done
 *   onError    {function} Called with error message string on failure
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Stack, TextField, Button, Typography, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import API from '../../../API';
import { COUNTRIES_TAX_CONFIG } from '../../../CreatorUser/CreatorPages/Dashboard/editCreator/constants';
import WiseFieldRenderer from './WiseFieldRenderer';

// Map ISO-3166-alpha2 to ISO-4217 currency code using COUNTRIES_TAX_CONFIG
function getCurrencyForCountry(countryCode) {
  const cfg = COUNTRIES_TAX_CONFIG[countryCode];
  return cfg?.currency || 'USD';
}

// Wise's schema returns dotted keys (e.g. 'address.firstLine') to indicate nested
// structure in the request body. We store them flat in form state for simplicity,
// then nest before sending — otherwise Wise sees no address fields and rejects with
// "We cannot currently accept payments to this recipient".
function nestDottedKeys(flat) {
  const out = {};
  Object.entries(flat).forEach(([key, value]) => {
    if (!key.includes('.')) {
      out[key] = value;
      return;
    }
    const parts = key.split('.');
    let cursor = out;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        cursor[part] = value;
      } else {
        cursor[part] = cursor[part] || {};
        cursor = cursor[part];
      }
    });
  });
  return out;
}

export default function WiseRecipientForm({ country, onComplete, onError }) {
  const currency = getCurrencyForCountry(country);

  const [requirements, setRequirements] = useState(null); // list of Wise account types
  const [selectedType, setSelectedType] = useState('');   // chosen account type key
  const [formValues, setFormValues] = useState({});
  const [accountHolderName, setAccountHolderName] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Ref so onError changes never cause fetchRequirements to be recreated,
  // which would re-trigger the effect and loop when the API returns an error.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const fetchRequirements = useCallback((targetCurrency) => {
    setLoading(true);
    setError(null);
    setRequirements(null);
    setSelectedType('');
    setFormValues({});

    API.wise.getAccountRequirements({ source: 'USD', target: targetCurrency, sourceAmount: 100 })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRequirements(list);
        if (list.length === 1) setSelectedType(list[0].type);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Failed to load bank field requirements.';
        setError(msg);
        onErrorRef.current?.(msg);
      })
      .finally(() => setLoading(false));
  }, []); // stable — onError called via ref to avoid recreating this on every parent render

  useEffect(() => {
    fetchRequirements(currency);
  }, [fetchRequirements]);

  // Re-fetch with current form values when a refreshRequirementsOnChange field changes
  const handleRefreshNeeded = useCallback((changedKey, newValue) => {
    const updatedValues = { ...formValues, [changedKey]: newValue };
    API.wise.refreshAccountRequirements({
      source: 'USD',
      target: currency,
      details: nestDottedKeys(updatedValues),
    }).then((data) => {
      const list = Array.isArray(data) ? data : [];
      setRequirements(list);
    }).catch((err) => {
      // Non-fatal for the form, but log so a stale schema doesn't silently
      // cause a downstream 422 on recipient creation.
      // eslint-disable-next-line no-console
      console.warn('[Wise] refreshAccountRequirements failed:',
        err?.response?.data?.error || err?.message || err);
    });
  }, [currency, formValues]);

  const handleFieldChange = (key) => (value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  // Memoized so the array reference only changes when requirements or selectedType changes.
  // Without useMemo, flatMap() produces a new reference every render, causing the country-sync
  // effect below to fire on every render and loop infinitely via setFormValues.
  const allFields = useMemo(() => {
    const selectedRequirement = requirements?.find((r) => r.type === selectedType);
    return selectedRequirement
      ? selectedRequirement.fields.flatMap((f) => f.group || [])
      : [];
  }, [requirements, selectedType]);

  // Country-type fields from Wise (e.g. address.country) are auto-filled from the
  // country prop and excluded from the rendered form — the greyed-out country
  // display at the top already communicates this to the user.
  const isCountryField = (key) => key === 'address.country' || key === 'country';

  // Keep formValues in sync: country fields are always the selected country.
  useEffect(() => {
    const countryFields = allFields.filter((f) => isCountryField(f.key));
    if (countryFields.length === 0) return;
    setFormValues((prev) => {
      const patch = {};
      let changed = false;
      countryFields.forEach((f) => {
        if (prev[f.key] !== country) { patch[f.key] = country; changed = true; }
      });
      return changed ? { ...prev, ...patch } : prev;
    });
  }, [allFields, country]);

  const hasFullName = accountHolderName.trim().split(/\s+/).length >= 2;

  const isValid =
    hasFullName &&
    selectedType &&
    allFields.filter((f) => f.required).every((f) => formValues[f.key]?.toString().trim());

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const data = await API.wise.createRecipient({
        currency,
        country,
        accountHolderName: accountHolderName.trim(),
        requirementType: selectedType,
        details: nestDottedKeys(formValues),
        nickname: nickname.trim() || undefined,
        set_as_primary: false,
      });
      onComplete && onComplete(data.wallet);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add bank account. Please try again.';
      setError(msg);
      onError && onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Bank Account Details
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Your bank details are stored securely and used only for payout processing.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="caption" color="text.secondary">
              Loading bank account fields for {country}…
            </Typography>
          </Box>
        )}

        {!loading && requirements && requirements.length > 0 && (
          <>
            {/* Account type selector (shown only when multiple types available) */}
            {requirements.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    setFormValues({});
                  }}
                  label="Account Type"
                  sx={{ borderRadius: 2 }}
                >
                  {requirements.map((r) => (
                    <MenuItem key={r.type} value={r.type}>
                      {r.title || r.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Account holder name */}
            <TextField
              label="Account Holder Full Name"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              fullWidth
              size="small"
              required
              placeholder="e.g. Jane Smith"
              helperText={accountHolderName && !hasFullName ? 'Please enter first and last name' : ''}
              error={accountHolderName.length > 0 && !hasFullName}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {/* Country-specific dynamic fields (country fields are auto-filled above) */}
            {selectedType && allFields.filter((f) => !isCountryField(f.key)).map((field) => (
              <WiseFieldRenderer
                key={field.key}
                field={field}
                value={formValues[field.key] || ''}
                onChange={handleFieldChange(field.key)}
                onRefreshNeeded={field.refreshRequirementsOnChange ? handleRefreshNeeded : undefined}
              />
            ))}

            {/* Optional nickname */}
            <TextField
              label="Nickname (optional)"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              fullWidth
              size="small"
              placeholder={`My ${country} Account`}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Verification pending — payouts via this account will be processed manually
              until verification is complete.
            </Alert>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              fullWidth
              sx={{ borderRadius: 2 }}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {submitting ? 'Adding Bank Account…' : 'Add Bank Account'}
            </Button>
          </>
        )}

        {!loading && requirements && requirements.length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            No supported bank account types found for this country/currency combination.
            Please contact support.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
